import { useQuery } from '@tanstack/react-query';
import { VITAL_SIGNS, type VitalKey } from '@wodi/domain';
import type { ClinicalAlertRule, DangerSignView, VitalSignSetView } from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogActions,
  Notice,
  TextArea,
  TextField,
  VitalField,
} from '@wodi/ui';
import { formatEatTime, formatIsoDay, eatDayOf } from '@wodi/domain';
import { useMemo, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { clinicalRulesQuery } from '../api/triage.ts';
import { eatInstant } from '../imaging/format.ts';
import { flagWords } from '../inpatient/vitals-chart.tsx';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import {
  ALERT_FALLBACK,
  bloodPressureError,
  criticalVitalsAlert,
  domainRules,
  judgeField,
  vitalInputs,
  type VitalTexts,
} from '../triage/triage-model.ts';
import styles from './maternity.module.css';

// What the maternity and MCH screens share (docs/plans/phase2-maternity-mch.md §6): the
// vitals block (the triage fields and rules, inside a larger form), the danger-sign checklist
// (only the facility's list; "not set up" when empty), clinical time fields, and the
// entered-in-error dialog. No clinical number lives here.

export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * A save error to show under the form, or null: a critical-value refusal is answered by the
 * reason fields that appear, not by a second message.
 */
export function saveErrorText(error: unknown, fallback: string): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError && error.code === 'alert_acknowledgement_required') return null;
  return errorText(error, fallback);
}

/** Whole numbers typed in a field: null when empty, 'invalid' when not a whole number. */
export function readWhole(text: string): number | null | 'invalid' {
  const t = text.trim();
  if (t === '') return null;
  return /^\d{1,5}$/.test(t) ? Number(t) : 'invalid';
}

/** A decimal typed in a field (dot decimal), or null / 'invalid'. */
export function readDecimal(text: string): number | null | 'invalid' {
  const t = text.trim();
  if (t === '') return null;
  return /^\d{1,3}(\.\d{1,2})?$/.test(t) ? Number(t) : 'invalid';
}

export function setValues(set: VitalSignSetView | null): string {
  if (set === null) return 'No vitals';
  const current = set.observations.filter((o) => o.status === 'final');
  if (current.length === 0) return 'No values';
  return current
    .map((o) => {
      const flag = flagWords(o.interpretation);
      return `${VITAL_SIGNS[o.key].label} ${o.value} ${o.unit.display}${flag === null ? '' : ` (${flag})`}`;
    })
    .join(', ');
}

// --- Vitals inside a maternity form -------------------------------------------------------------

export interface EmbeddedVitals {
  texts: VitalTexts;
  setTexts: (next: VitalTexts) => void;
  reasons: Partial<Record<ClinicalAlertRule, string>>;
  setReasons: (next: Partial<Record<ClinicalAlertRule, string>>) => void;
  serverAlerts: ClinicalAlertRule[];
  setServerAlerts: (next: ClinicalAlertRule[]) => void;
  ageDays: number | null;
}

export function useEmbeddedVitals(ageDays: number | null): EmbeddedVitals {
  const [texts, setTexts] = useState<VitalTexts>({});
  const [reasons, setReasons] = useState<Partial<Record<ClinicalAlertRule, string>>>({});
  const [serverAlerts, setServerAlerts] = useState<ClinicalAlertRule[]>([]);
  return { texts, setTexts, reasons, setReasons, serverAlerts, setServerAlerts, ageDays };
}

export const MATERNAL_VITALS: readonly VitalKey[] = [
  'body_weight',
  'body_temperature',
  'heart_rate',
  'bp_systolic',
  'bp_diastolic',
  'respiratory_rate',
  'oxygen_saturation',
];

/** What the form sends, and whether it may be sent (no field error, reasons given). */
export function embeddedPayload(state: EmbeddedVitals, alerts: ReturnType<typeof useVitalAlerts>) {
  const inputs = vitalInputs(state.texts, null);
  if (inputs.length === 0) return { vitals: null, ready: true };
  const reasonOf = (id: ClinicalAlertRule) => state.reasons[id]?.trim() ?? '';
  const ready =
    !alerts.fieldError &&
    alerts.bpError === null &&
    alerts.list.every((a) => reasonOf(a.ruleId).length >= REASON_MIN_LENGTH);
  return {
    vitals: {
      vitals: inputs,
      acknowledgements: alerts.list.map((a) => ({ ruleId: a.ruleId, reason: reasonOf(a.ruleId) })),
    },
    ready,
  };
}

export function useVitalAlerts(state: EmbeddedVitals, keys: readonly VitalKey[]) {
  const rulesView = useQuery(clinicalRulesQuery);
  const rules = useMemo(() => domainRules(rulesView.data?.rules ?? []), [rulesView.data]);
  const critical = criticalVitalsAlert({
    texts: state.texts,
    glucoseContext: null,
    rules,
    ageDays: state.ageDays,
  });
  const foreseen = critical === null ? [] : [critical];
  const list = [
    ...foreseen,
    ...state.serverAlerts
      .filter((id) => !foreseen.some((a) => a.ruleId === id))
      .map((ruleId) => ({ ruleId, ...ALERT_FALLBACK[ruleId] })),
  ];
  const fieldError = keys.some(
    (key) =>
      judgeField(key, state.texts[key] ?? '', rules, state.ageDays, null).error !== undefined,
  );
  return { rules, list, fieldError, bpError: bloodPressureError(state.texts) };
}

/** Records the critical alerts the server asked for, so their reason fields appear. */
export function noteServerAlerts(error: unknown, state: EmbeddedVitals): void {
  if (error instanceof ApiError && error.code === 'alert_acknowledgement_required') {
    const ids = error.context?.['ruleIds'];
    if (Array.isArray(ids)) state.setServerAlerts(ids as ClinicalAlertRule[]);
  }
}

export function EmbeddedVitalsFields({
  state,
  keys,
  tried,
}: {
  state: EmbeddedVitals;
  keys: readonly VitalKey[];
  tried: boolean;
}) {
  const alerts = useVitalAlerts(state, keys);
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>Vitals</legend>
      <p className={styles.meta}>
        Checked against the clinical rules as you type. Leave a field empty if it was not taken.
      </p>
      <div className={styles.grid}>
        {keys.map((key) => {
          const definition = VITAL_SIGNS[key];
          const judged = judgeField(key, state.texts[key] ?? '', alerts.rules, state.ageDays, null);
          const error =
            judged.error ?? (key === 'bp_systolic' ? alerts.bpError : null) ?? undefined;
          return (
            <VitalField
              key={key}
              label={definition.label}
              unit={definition.unitDisplay}
              value={state.texts[key] ?? ''}
              onChange={(value) => {
                state.setTexts({ ...state.texts, [key]: value });
              }}
              provisional={judged.provisional}
              {...(judged.flag === undefined ? {} : { flag: judged.flag })}
              {...(error === undefined ? {} : { errorMessage: error })}
            />
          );
        })}
      </div>
      {alerts.list.map((alert) => (
        <AlertPanel
          key={alert.ruleId}
          title={alert.title}
          danger={alert.danger}
          reason={state.reasons[alert.ruleId] ?? ''}
          onReasonChange={(value) => {
            state.setReasons({ ...state.reasons, [alert.ruleId]: value });
          }}
          {...(tried && (state.reasons[alert.ruleId]?.trim() ?? '').length < REASON_MIN_LENGTH
            ? { reasonError: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.` }
            : {})}
        >
          {alert.body}
        </AlertPanel>
      ))}
    </fieldset>
  );
}

// --- Danger signs -------------------------------------------------------------------------------

export function DangerSignChecklist({
  label,
  list,
  codes,
  onCodes,
  none,
  onNone,
}: {
  label: string;
  list: readonly DangerSignView[];
  codes: readonly string[];
  onCodes: (next: string[]) => void;
  none: boolean;
  onNone: (next: boolean) => void;
}) {
  if (list.length === 0) {
    return (
      <p className={styles.meta}>
        {`${label}: not set up. The clinical lead enters the list in Settings > Maternity and MCH.`}
      </p>
    );
  }
  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{label}</legend>
      <div className={styles.checks}>
        {list.map((sign) => (
          <Checkbox
            key={sign.code}
            isSelected={codes.includes(sign.code)}
            isDisabled={none}
            onChange={(on) => {
              onCodes(on ? [...codes, sign.code] : codes.filter((c) => c !== sign.code));
            }}
          >
            {sign.display}
          </Checkbox>
        ))}
        <Checkbox isSelected={none} isDisabled={codes.length > 0} onChange={onNone}>
          None of these
        </Checkbox>
      </div>
    </fieldset>
  );
}

export function DangerSignWords({
  signs,
  none,
}: {
  signs: readonly DangerSignView[];
  none: boolean;
}) {
  if (signs.length > 0)
    return <span className={styles.danger}>{signs.map((s) => s.display).join(', ')}</span>;
  return <span>{none ? 'None of the listed signs' : 'Not asked'}</span>;
}

// --- Clinical time ------------------------------------------------------------------------------

/**
 * Day and time fields that open on now. Left as they are, nothing is sent and the server
 * records the moment of saving; changed, the instant in East Africa Time.
 */
export function useWhen() {
  const [opened] = useState(() => {
    const now = new Date();
    return { day: formatIsoDay(eatDayOf(now)), time: formatEatTime(now) };
  });
  const [day, setDay] = useState<string | null>(opened.day);
  const [time, setTime] = useState(opened.time);
  const untouched = day === opened.day && time.trim() === opened.time;
  const at = day === null ? null : eatInstant(day, time);
  return {
    day,
    setDay,
    time,
    setTime,
    invalid: !untouched && at === null,
    /** The ISO instant to send, or undefined for "now". */
    value: untouched || at === null ? undefined : at.toISOString(),
    /** The instant shown, for fields that must always send one. */
    instant: at,
  };
}

export function WhenFields({ label, when }: { label: string; when: ReturnType<typeof useWhen> }) {
  return (
    <div className={styles.when}>
      <DateField label={`${label}: day`} value={when.day} onChange={when.setDay} />
      <TextField
        label={`${label}: time`}
        width="xs"
        value={when.time}
        onChange={when.setTime}
        description="24-hour, East Africa Time"
        {...(when.invalid ? { errorMessage: 'Write the time as 14:05.' } : {})}
      />
    </div>
  );
}

// --- Entered in error ---------------------------------------------------------------------------

export function InErrorDialog({
  what,
  onClose,
  onConfirm,
  pending,
  error,
}: {
  what: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  pending: boolean;
  error: unknown;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title={`Enter ${what} in error`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        It stays in the history, marked entered in error, with your reason.
      </p>
      <TextArea label="Why is it in error?" value={reason} onChange={setReason} rows={2} />
      {error === null ? null : (
        <Notice tone="error">{errorText(error, 'Nothing was changed. Try again.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Keep it
        </Button>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < REASON_MIN_LENGTH}
          isPending={pending}
          pendingLabel="Saving…"
          onPress={() => {
            onConfirm(reason.trim());
          }}
        >
          Enter in error
        </Button>
      </DialogActions>
    </Dialog>
  );
}
